import { useNavigation } from '@react-navigation/native';
import React, { useCallback, useEffect, useState } from 'react';
import {
  FlatList,
  Image,
  RefreshControl,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';

import { useAuth } from '../hooks/useAuth';
import { useSocket } from '../hooks/useSocket';
import groupService from '../services/group.service';
import httpClient from '../utils/httpClient';

interface Conversation {
  id: string;
  type: 'private' | 'group';
  name: string;
  avatar?: string;
  lastMessage?: string;
  lastMessageTime?: string;
  unreadCount?: number;
  lastMessageAt?: Date;
}

export default function HomeScreen() {
  const navigation = useNavigation();
  const { user: _user } = useAuth();
  const { on, off } = useSocket();

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [refreshing, setRefreshing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const formatTime = (date: Date | string | undefined): string => {
    if (!date) return '';
    const d = new Date(date);
    const now = new Date();
    const diff = now.getTime() - d.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) {
      return d.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' });
    } else if (days === 1) {
      return '昨天';
    } else if (days < 7) {
      return ['周日', '周一', '周二', '周三', '周四', '周五', '周六'][d.getDay()];
    } else {
      return d.toLocaleDateString('zh-CN', { month: '2-digit', day: '2-digit' });
    }
  };

  const loadConversations = useCallback(async () => {
    try {
      setError(null);
      setLoading(true);

      // 从服务器加载会话列表
      const response = await httpClient.get('/conversations');
      const data: Conversation[] = response.data?.conversations || response.data || [];

      // 格式化会话数据
      const formattedConversations = data.map((conv: any) => ({
        id: conv.id,
        type: conv.type || 'private',
        name: conv.name || conv.targetUser?.nickname || '未知',
        avatar: conv.avatar || conv.targetUser?.avatar,
        lastMessage: conv.lastMessage?.content || conv.lastMessage,
        lastMessageTime: formatTime(conv.lastMessage?.createdAt || conv.lastMessageAt),
        lastMessageAt: new Date(conv.lastMessage?.createdAt || conv.lastMessageAt),
        unreadCount: conv.unreadCount || 0,
      }));

      // 按最后消息时间排序
      formattedConversations.sort((a, b) => {
        const timeA = a.lastMessageAt?.getTime() || 0;
        const timeB = b.lastMessageAt?.getTime() || 0;
        return timeB - timeA;
      });

      setConversations(formattedConversations);

      // 同时加载群组列表
      try {
        await groupService.loadGroups();
      } catch (groupError) {
        console.warn('Failed to load groups:', groupError);
      }
    } catch (err: any) {
      console.error('Failed to load conversations:', err);
      setError(err.message || '加载会话列表失败');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // 监听新消息更新会话列表
  useEffect(() => {
    const handleNewMessage = (message: any) => {
      setConversations((prev) => {
        const conversationId = message.groupId || message.from;
        const existingIndex = prev.findIndex((c) => c.id === conversationId);

        if (existingIndex >= 0) {
          const updated = [...prev];
          updated[existingIndex] = {
            ...updated[existingIndex],
            lastMessage: message.content,
            lastMessageTime: formatTime(new Date()),
            lastMessageAt: new Date(),
            unreadCount: (updated[existingIndex].unreadCount || 0) + 1,
          };
          // 将更新的会话移到顶部
          const [item] = updated.splice(existingIndex, 1);
          return [item, ...updated];
        }
        return prev;
      });
    };

    on('message', handleNewMessage);

    return () => {
      off('message', handleNewMessage);
    };
  }, [on, off]);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  const handleRefresh = () => {
    setRefreshing(true);
    loadConversations();
  };

  if (loading && !refreshing) {
    return (
      <View style={styles.loadingContainer}>
        <Text style={styles.loadingText}>加载中...</Text>
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.errorContainer}>
        <Text style={styles.errorText}>{error}</Text>
        <TouchableOpacity style={styles.retryButton} onPress={loadConversations}>
          <Text style={styles.retryText}>重试</Text>
        </TouchableOpacity>
      </View>
    );
  }

  const handlePress = (conversation: Conversation) => {
    navigation.navigate('Chat' as any, {
      userId: conversation.type === 'private' ? conversation.id : undefined,
      groupId: conversation.type === 'group' ? conversation.id : undefined,
    });
  };

  const renderConversation = ({ item }: { item: Conversation }) => {
    return (
      <TouchableOpacity style={styles.conversation} onPress={() => handlePress(item)}>
        <View style={styles.avatarContainer}>
          <Image
            source={{ uri: item.avatar || 'https://via.placeholder.com/50' }}
            style={styles.avatar}
          />
          {item.unreadCount && item.unreadCount > 0 && (
            <View style={styles.badge}>
              <Text style={styles.badgeText}>
                {item.unreadCount > 99 ? '99+' : item.unreadCount}
              </Text>
            </View>
          )}
        </View>
        <View style={styles.conversationInfo}>
          <View style={styles.conversationHeader}>
            <Text style={styles.name} numberOfLines={1}>
              {item.name}
            </Text>
            <Text style={styles.time}>{item.lastMessageTime}</Text>
          </View>
          <Text style={styles.lastMessage} numberOfLines={1}>
            {item.lastMessage}
          </Text>
        </View>
      </TouchableOpacity>
    );
  };

  return (
    <View style={styles.container}>
      <FlatList
        data={conversations}
        renderItem={renderConversation}
        keyExtractor={(item) => item.id}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={handleRefresh} />}
        ListEmptyComponent={
          <View style={styles.empty}>
            <Text style={styles.emptyText}>暂无会话</Text>
          </View>
        }
      />
    </View>
  );
}

const styles = StyleSheet.create({
  avatar: {
    borderRadius: 24,
    height: 48,
    width: 48,
  },
  avatarContainer: {
    marginRight: 12,
    position: 'relative',
  },
  badge: {
    alignItems: 'center',
    backgroundColor: '#ff4d4f',
    borderRadius: 10,
    height: 20,
    justifyContent: 'center',
    minWidth: 20,
    paddingHorizontal: 4,
    position: 'absolute',
    right: -4,
    top: -4,
  },
  badgeText: {
    color: '#fff',
    fontSize: 11,
    fontWeight: 'bold',
  },
  container: {
    backgroundColor: '#fff',
    flex: 1,
  },
  conversation: {
    alignItems: 'center',
    borderBottomColor: '#f5f5f5',
    borderBottomWidth: StyleSheet.hairlineWidth,
    flexDirection: 'row',
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  conversationHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  conversationInfo: {
    flex: 1,
  },
  empty: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
    paddingVertical: 100,
  },
  emptyText: {
    color: '#999',
    fontSize: 16,
  },
  errorContainer: {
    alignItems: 'center',
    backgroundColor: '#fff',
    flex: 1,
    justifyContent: 'center',
    padding: 20,
  },
  errorText: {
    color: '#ff4d4f',
    fontSize: 16,
    marginBottom: 16,
    textAlign: 'center',
  },
  lastMessage: {
    color: '#666',
    fontSize: 14,
  },
  loadingContainer: {
    alignItems: 'center',
    backgroundColor: '#fff',
    flex: 1,
    justifyContent: 'center',
  },
  loadingText: {
    color: '#999',
    fontSize: 16,
  },
  name: {
    color: '#333',
    flex: 1,
    fontSize: 16,
    fontWeight: 'bold',
  },
  retryButton: {
    backgroundColor: '#007AFF',
    borderRadius: 8,
    paddingHorizontal: 24,
    paddingVertical: 12,
  },
  retryText: {
    color: '#fff',
    fontSize: 16,
  },
  time: {
    color: '#999',
    fontSize: 12,
  },
});
